import { beddaysEventIcon, beddaysPathwayIcon, costEventIcon, costPathwayIcon, diedIcon, Dimension, firstAppointmentsIcon, followupAppointmentsIcon, patientPercentIcon } from './PathwayIcons';
import { DestinationMetric } from './types/EventMetric';

const indexEventWidth = 200
const eventWidth = 300
const eventHeaderBoxWidth = 200
const legendBoxWidth = 150
const metricBoxHeaderHeight = 40
const metricBoxWidth = 140
const iconSize = 14
const actionIconSize = 25
const radius = 4

const event = {
    dividerHeight: 750,
    width: eventWidth,
    header: {
        height: 20,
        width: eventHeaderBoxWidth,
        x: centreWidth(eventWidth, eventHeaderBoxWidth),
        padding: {
            top: 10
        },
        font: {
            size: 12,
            fontFamily: 'sans-serif'
        },
        metrics: {
            height: metricBoxHeaderHeight,
            width: eventHeaderBoxWidth,
            font: {
                size: 12,
                fontFamily: 'sans-serif',
                colour: '#636b72'
            },
            subFont: {
                size: 8,
                fontFamily: 'sans-serif',
                colour: '#788496'
            }
        }
    }
}

export const pathwayTheme = {
    index: {
        width: indexEventWidth,
        x: centreWidth(indexEventWidth, metricBoxWidth),
        legend: {
            width: legendBoxWidth,
            x: centreWidth(indexEventWidth, legendBoxWidth),
        },
    },
    event: event,
    metric: {
        padding: {
            left: 10,
        },
        x: centreWidth(event.width, metricBoxWidth),
        header: {
            height: metricBoxHeaderHeight,
            width: metricBoxWidth,
            radius: radius,
            font: {
                size: 11,
                fontFamily: 'sans-serif'
            },
            subFont: {
                size: 14,
                fontFamily: 'sans-serif',
                fontWeight: 'bold'
            },
        },
        content: {
            height: (numberOfMetrics: number) => { return numberOfMetrics * iconSize * 1.5 },
            width: metricBoxWidth,
            radius: radius,
            icon: {
                size: iconSize
            },
            font: {
                size: 12,
                fontFamily: 'sans-serif'
            }
        },
        actions: {
            filter: {
                icon: {
                    x: metricBoxWidth - actionIconSize / 2,
                    y: metricBoxHeaderHeight / 2 - actionIconSize / 2,
                    size: actionIconSize
                }
            },
            include: {
                icon: {
                    x: metricBoxWidth - actionIconSize / 2,
                    y: (height: number) => { return metricBoxHeaderHeight + height / 2 - actionIconSize / 2 },
                    size: actionIconSize
                }
            }
        }
    },
    dataset: {
        '-2': {
            title: '',
            colour: '#d4d4d4'
        },
        '-1': {
            title: 'Index',
            colour: '#79c80e'
        },
        '1': {
            title: 'A&E',
            colour: '#ff3669'
        },
        '2': {
            title: 'Inpatient Non-Elective',
            colour: '#ff8736'
        },
        '3': {
            title: 'Inpatient Elective',
            colour: '#7380d9'
        },
        '4': {
            title: 'Outpatient',
            colour: '#00bbaf'
        }
    }

}

export function getDatasetTheme(dataset: number): { title: string, colour: string } {
    // @ts-ignore
    return pathwayTheme.dataset['' + dataset]
}

export function getNoDataTheme(): { title: string, colour: string } {
    return pathwayTheme.dataset['-2']
}

interface LengendData {
    icon: (selection: d3.Selection<SVGGElement, any, null, undefined>, dimensions: Dimension) => void
    label: Array<String>
}

export const lengendData: LengendData[] = [
    {
        icon: patientPercentIcon,
        label: ['% of patients']
    },
    {
        icon: costEventIcon,
        label: ['Average cost', 'per event']
    },
    {
        icon: costPathwayIcon,
        label: ['Average cost of', 'pathway']
    },
    {
        icon: beddaysEventIcon,
        label: ['Average LOS per', 'event']
    },
    {
        icon: beddaysPathwayIcon,
        label: ['Average LOS of', 'pathway']
    },
    {
        icon: diedIcon,
        label: ['Number of patients', 'died at this event']
    },
    {
        icon: firstAppointmentsIcon,
        label: ['% First appointments']
    },
    {
        icon: followupAppointmentsIcon,
        label: ['% Follow up appointments']
    }
]

export interface DatasetMetricConfigType {
    [key: number]: MetricConfigType[];
}

export interface MetricConfigType {
    icon: (selection: d3.Selection<SVGGElement, any, null, undefined>, dimensions: Dimension) => void
    value(d: DestinationMetric): string
    suppressed?: boolean
}

const commonMetricConfig = [
    {
        icon: patientPercentIcon,
        value: (d: DestinationMetric) => { return `${d.patientCountPercent}%` }
    },
    {
        icon: costEventIcon,
        value: (d: DestinationMetric) => { return `£${d.avgEventCost.toLocaleString()}` }
    },
    {
        icon: costPathwayIcon,
        value: (d: DestinationMetric) => { return `£${d.avgPathwayCost.toLocaleString()}` }
    }
]

const inpatientMetricConfig = [
    ...commonMetricConfig,
    {
        icon: diedIcon,
        value: (d: DestinationMetric) => { return `${d.deathCount!.toLocaleString()}` },
        suppressed: true
    },
    {
        icon: beddaysEventIcon,
        value: (d: DestinationMetric) => { return `${d.avgBeddays!.toLocaleString()}` }
    },
    {
        icon: beddaysPathwayIcon,
        value: (d: DestinationMetric) => { return `${d.avgPathwayBeddays!.toLocaleString()}` }
    }
]
export const metricConfig: DatasetMetricConfigType = {
    '-1': [],
    1: [
        ...commonMetricConfig
    ],
    2: inpatientMetricConfig,
    3: inpatientMetricConfig,
    4: [
        ...commonMetricConfig,
        {
            icon: firstAppointmentsIcon,
            value: (d: DestinationMetric) => { return `${d.firstAppointmentPercent}%` }
        },
        {
            icon: followupAppointmentsIcon,
            value: (d: DestinationMetric) => { return `${d.followupAppointmentPercent!}%` }
        },
    ],
}

export function centreWidth(parent: number, child: number) {
    return parent / 2 - child / 2
}